import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { getKPIs } from '../../lib/contabilidadApi'

const KPI_LABELS = {
  liquidez_corriente:  'Liquidez Corriente',
  prueba_acida:        'Prueba Ácida',
  rotacion_inventario: 'Rotación Inventario',
  rentabilidad_pct:    'Rentabilidad %',
  dias_cobro:          'Días de Cobro',
  ciclo_efectivo_dias: 'Ciclo de Efectivo (días)',
}

const KPI_FMT = {
  liquidez_corriente:  (v) => v?.toFixed(2),
  prueba_acida:        (v) => v?.toFixed(2),
  rotacion_inventario: (v) => v?.toFixed(2),
  rentabilidad_pct:    (v) => v?.toFixed(1) + '%',
  dias_cobro:          (v) => v?.toFixed(0) + ' d',
  ciclo_efectivo_dias: (v) => v?.toFixed(0) + ' d',
}

const COLOR_BG = { verde: '#dcfce7', amarillo: '#fef3c7', rojo: '#fee2e2', sin_dato: '#f1f5f9' }
const COLOR_FG = { verde: '#166534', amarillo: '#92400e', rojo: '#991b1b', sin_dato: '#64748b' }
const COLOR_DOT = { verde: '#16a34a', amarillo: '#d97706', rojo: '#dc2626', sin_dato: '#94a3b8' }

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const ym = (d) => new Date(d).toISOString().slice(0, 10)
const firstDayMonth = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
const lastDayMonth = (d = new Date()) => {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return ym(last)
}

export default function ReportesContables() {
  const [tab, setTab] = useState('kpis')
  return (
    <AdminLayout>
      <div className="rc-wrap">
        <div className="rc-headline">
          <h1>Reportes contables</h1>
          <p>Tablero KPIs, Balance General, Estado de Resultados, Mayor por cuenta y Diario cronológico.</p>
        </div>
        <div className="rc-tabs">
          <button className={tab === 'kpis' ? 'active' : ''} onClick={() => setTab('kpis')}>🎯 KPIs</button>
          <button className={tab === 'balance' ? 'active' : ''} onClick={() => setTab('balance')}>📊 Balance General</button>
          <button className={tab === 'pyl' ? 'active' : ''} onClick={() => setTab('pyl')}>📈 Estado de Resultados</button>
          <button className={tab === 'mayor' ? 'active' : ''} onClick={() => setTab('mayor')}>📒 Mayor por cuenta</button>
          <button className={tab === 'diario' ? 'active' : ''} onClick={() => setTab('diario')}>📋 Diario</button>
        </div>
        {tab === 'kpis' && <TableroKPIs />}
        {tab === 'balance' && <BalanceGeneral />}
        {tab === 'pyl' && <EstadoResultados />}
        {tab === 'mayor' && <MayorCuenta />}
        {tab === 'diario' && <Diario />}
      </div>
      <style>{CSS}</style>
    </AdminLayout>
  )
}

// ════════ TABLERO 6 KPIs CON SEMÁFOROS ════════

export function TableroKPIs() {
  const [fecha, setFecha] = useState(() => ym(new Date()))
  const [kpis, setKpis] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { cargar() }, [fecha])

  async function cargar() {
    setLoading(true)
    setKpis(await getKPIs(fecha))
    setLoading(false)
  }

  const stats = useMemo(() => {
    const c = { verde: 0, amarillo: 0, rojo: 0, sin_dato: 0 }
    kpis.forEach(k => { c[k.color] = (c[k.color] || 0) + 1 })
    return c
  }, [kpis])

  return (
    <div>
      <div className="rc-filtros">
        <label>
          <span>Al día</span>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
        </label>
        <button className="rc-btn-ghost" onClick={cargar}>↻ Recalcular</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, fontSize: 12, color: '#475569' }}>
          <span><span className="rc-dot" style={{ background: COLOR_DOT.verde }}></span> {stats.verde}</span>
          <span><span className="rc-dot" style={{ background: COLOR_DOT.amarillo }}></span> {stats.amarillo}</span>
          <span><span className="rc-dot" style={{ background: COLOR_DOT.rojo }}></span> {stats.rojo}</span>
          <span><span className="rc-dot" style={{ background: COLOR_DOT.sin_dato }}></span> {stats.sin_dato}</span>
        </div>
      </div>

      {loading ? <div className="rc-loading">Calculando…</div> : (
        <div className="rc-kpis-grid">
          {kpis.map(k => (
            <div
              key={k.kpi}
              className="rc-kpi-card"
              style={{ background: COLOR_BG[k.color], borderColor: COLOR_FG[k.color] + '40' }}
            >
              <div className="rc-kpi-head">
                <span className="rc-kpi-name" style={{ color: COLOR_FG[k.color] }}>
                  <span className="rc-dot" style={{ background: COLOR_DOT[k.color] }}></span>
                  {KPI_LABELS[k.kpi] || k.kpi}
                </span>
              </div>
              <div className="rc-kpi-valor" style={{ color: COLOR_FG[k.color] }}>
                {k.valor != null ? (KPI_FMT[k.kpi]?.(k.valor) ?? k.valor) : '—'}
              </div>
              <div className="rc-kpi-formula">{k.formula}</div>
              <div className="rc-kpi-detalle">{k.detalle}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rc-info">
        <strong>📚 Sobre los KPIs</strong>
        <p>
          <b>Umbrales (Roberto criterio #3):</b>
          <br />Liquidez & Prueba Ácida: &gt;1 verde · =1 amarillo · &lt;1 rojo.
          <br />Rotación Inventario: &lt;6 verde · =6 amarillo · &gt;6 rojo (ideal &lt;2 meses permanencia).
          <br />Rentabilidad: &gt;45% verde · =45% amarillo · &lt;45% rojo.
          <br />Días de Cobro & Ciclo Efectivo: solo cuando haya ventas a crédito + crédito de proveedores.
        </p>
      </div>
    </div>
  )
}

// ════════ BALANCE GENERAL ════════

export function BalanceGeneral() {
  const [fecha, setFecha] = useState(() => ym(new Date()))
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandidos, setExpandidos] = useState(() => new Set())

  useEffect(() => { cargar() }, [fecha])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase.rpc('get_balance_general', { p_fecha: fecha })
    setFilas(data || [])
    setLoading(false)
  }

  // Filtrar visibles según expansión (mostrar nivel 1-2, niveles >=3 solo si parent está expandido)
  const visibles = useMemo(() => {
    return filas.filter(f => {
      if (f.nivel <= 2) return true
      let parent = f.parent_codigo
      while (parent) {
        if (!expandidos.has(parent)) return false
        const p = filas.find(x => x.codigo === parent)
        if (!p) break
        parent = p.parent_codigo
      }
      return true
    })
  }, [filas, expandidos])

  const totales = useMemo(() => {
    const activo = filas.find(f => f.codigo === '1')?.saldo || 0
    const pasivo = filas.find(f => f.codigo === '2')?.saldo || 0
    const patrimonio = filas.find(f => f.codigo === '3')?.saldo || 0
    return { activo, pasivo, patrimonio, diferencia: activo - (pasivo + patrimonio) }
  }, [filas])

  function toggle(codigo) {
    setExpandidos(prev => {
      const next = new Set(prev)
      if (next.has(codigo)) next.delete(codigo)
      else next.add(codigo)
      return next
    })
  }

  function expandirTodo() {
    setExpandidos(new Set(filas.filter(f => f.nivel < 4).map(f => f.codigo)))
  }
  function colapsarTodo() { setExpandidos(new Set()) }

  return (
    <>
      <div className="rc-toolbar">
        <label className="rc-fil"><span>Fecha de corte</span><input type="date" value={fecha} onChange={e => setFecha(e.target.value)} /></label>
        <button className="rc-btn-ghost" onClick={expandirTodo}>Expandir todo</button>
        <button className="rc-btn-ghost" onClick={colapsarTodo}>Colapsar todo</button>
      </div>

      <div className="rc-kpis">
        <KpiCard label="Activo" value={'$' + fmt(totales.activo)} color="#1e40af" />
        <KpiCard label="Pasivo" value={'$' + fmt(totales.pasivo)} color="#dc2626" />
        <KpiCard label="Patrimonio" value={'$' + fmt(totales.patrimonio)} color="#16a34a" />
        <KpiCard label="Diferencia" value={'$' + fmt(totales.diferencia)} color={Math.abs(totales.diferencia) < 0.01 ? '#16a34a' : '#dc2626'} sub={Math.abs(totales.diferencia) < 0.01 ? '✓ Balanceado' : '⚠ Revisar'} />
      </div>

      {loading ? <div className="rc-loading">Cargando…</div> : (
        <div className="rc-tabla-wrap">
          <table className="rc-tabla">
            <thead>
              <tr>
                <th>Código</th>
                <th>Cuenta</th>
                <th style={{ textAlign: 'right' }}>Debe</th>
                <th style={{ textAlign: 'right' }}>Haber</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(f => {
                const tieneHijos = filas.some(x => x.parent_codigo === f.codigo)
                const exp = expandidos.has(f.codigo)
                const indent = (f.nivel - 1) * 16
                const esTotal = f.nivel === 1
                const bold = f.nivel <= 2
                return (
                  <tr key={f.codigo} className={esTotal ? 'rc-row-total' : ''}>
                    <td>
                      <div style={{ paddingLeft: indent, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {tieneHijos ? (
                          <button className="rc-expand" onClick={() => toggle(f.codigo)}>{exp ? '▼' : '▶'}</button>
                        ) : <span style={{ width: 14 }} />}
                        <code style={{ fontWeight: bold ? 900 : 700 }}>{f.codigo}</code>
                      </div>
                    </td>
                    <td style={{ fontWeight: bold ? 800 : 500 }}>{f.nombre}</td>
                    <td style={{ textAlign: 'right' }}>${fmt(f.total_debe)}</td>
                    <td style={{ textAlign: 'right' }}>${fmt(f.total_haber)}</td>
                    <td style={{ textAlign: 'right', fontWeight: bold ? 900 : 700, color: f.saldo < 0 ? '#dc2626' : '#1A3461' }}>${fmt(f.saldo)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="rc-info">
        <strong>ℹ Notas sobre el balance</strong>
        <p>El Activo (lo que la empresa tiene) debe ser igual al Pasivo (lo que debe) más el Patrimonio (capital + resultados). Si la diferencia no es 0, hay un asiento desbalanceado en algún lado. Las cuentas con saldo NEGATIVO suelen ser regularizadoras (ej: amortizaciones acumuladas) o errores.</p>
      </div>
    </>
  )
}

// ════════ ESTADO DE RESULTADOS ════════

export function EstadoResultados() {
  const [desde, setDesde] = useState(() => firstDayMonth())
  const [hasta, setHasta] = useState(() => lastDayMonth())
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandidos, setExpandidos] = useState(() => new Set(['4', '5']))

  useEffect(() => { cargar() }, [desde, hasta])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase.rpc('get_estado_resultados', { p_desde: desde, p_hasta: hasta })
    setFilas(data || [])
    setLoading(false)
  }

  const visibles = useMemo(() => {
    return filas.filter(f => {
      if (f.nivel <= 2) return true
      let parent = f.parent_codigo
      while (parent) {
        if (!expandidos.has(parent)) return false
        const p = filas.find(x => x.codigo === parent)
        if (!p) break
        parent = p.parent_codigo
      }
      return true
    })
  }, [filas, expandidos])

  const totales = useMemo(() => {
    const ingresos = filas.find(f => f.codigo === '4')?.saldo || 0
    const egresos = filas.find(f => f.codigo === '5')?.saldo || 0
    const resultado = ingresos - egresos
    return { ingresos, egresos, resultado, margen: ingresos > 0 ? (resultado / ingresos) * 100 : 0 }
  }, [filas])

  function toggle(codigo) {
    setExpandidos(prev => {
      const next = new Set(prev)
      if (next.has(codigo)) next.delete(codigo)
      else next.add(codigo)
      return next
    })
  }

  return (
    <>
      <div className="rc-toolbar">
        <label className="rc-fil"><span>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} /></label>
        <label className="rc-fil"><span>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} /></label>
        <button className="rc-btn-ghost" onClick={() => { setDesde(firstDayMonth()); setHasta(lastDayMonth()) }}>Este mes</button>
        <button className="rc-btn-ghost" onClick={() => {
          const d = new Date(); d.setMonth(d.getMonth() - 1)
          setDesde(firstDayMonth(d)); setHasta(lastDayMonth(d))
        }}>Mes anterior</button>
        <button className="rc-btn-ghost" onClick={() => {
          const d = new Date()
          setDesde(`${d.getFullYear()}-01-01`); setHasta(ym(new Date()))
        }}>Año actual</button>
      </div>

      <div className="rc-kpis">
        <KpiCard label="Ingresos" value={'$' + fmt(totales.ingresos)} color="#16a34a" />
        <KpiCard label="Egresos" value={'$' + fmt(totales.egresos)} color="#dc2626" />
        <KpiCard label="Resultado" value={'$' + fmt(totales.resultado)} color={totales.resultado >= 0 ? '#16a34a' : '#dc2626'} sub={totales.resultado >= 0 ? '✓ Utilidad' : '⚠ Pérdida'} />
        <KpiCard label="Margen %" value={totales.margen.toFixed(1) + '%'} color="#7c3aed" sub="sobre ingresos" />
      </div>

      {loading ? <div className="rc-loading">Cargando…</div> : (
        <div className="rc-tabla-wrap">
          <table className="rc-tabla">
            <thead>
              <tr>
                <th>Código</th>
                <th>Cuenta</th>
                <th style={{ textAlign: 'right' }}>Saldo del período</th>
                <th style={{ textAlign: 'right', width: 80 }}>% s/Ingr</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(f => {
                const tieneHijos = filas.some(x => x.parent_codigo === f.codigo)
                const exp = expandidos.has(f.codigo)
                const indent = (f.nivel - 1) * 16
                const bold = f.nivel <= 2
                const pct = totales.ingresos > 0 ? (Number(f.saldo) / totales.ingresos) * 100 : 0
                return (
                  <tr key={f.codigo} className={f.nivel === 1 ? 'rc-row-total' : ''}>
                    <td>
                      <div style={{ paddingLeft: indent, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {tieneHijos ? (
                          <button className="rc-expand" onClick={() => toggle(f.codigo)}>{exp ? '▼' : '▶'}</button>
                        ) : <span style={{ width: 14 }} />}
                        <code style={{ fontWeight: bold ? 900 : 700 }}>{f.codigo}</code>
                      </div>
                    </td>
                    <td style={{ fontWeight: bold ? 800 : 500 }}>{f.nombre}</td>
                    <td style={{ textAlign: 'right', fontWeight: bold ? 900 : 600, color: f.tipo === 'egreso' ? '#dc2626' : '#16a34a' }}>${fmt(f.saldo)}</td>
                    <td style={{ textAlign: 'right', fontSize: 11, color: '#64748b' }}>{pct.toFixed(1)}%</td>
                  </tr>
                )
              })}
              <tr className="rc-row-resultado">
                <td colSpan={2}><b>RESULTADO DEL PERÍODO (Ingresos − Egresos)</b></td>
                <td style={{ textAlign: 'right', fontWeight: 900, fontSize: 16, color: totales.resultado >= 0 ? '#16a34a' : '#dc2626' }}>${fmt(totales.resultado)}</td>
                <td style={{ textAlign: 'right', fontSize: 11, color: '#64748b' }}>{totales.margen.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ════════ MAYOR POR CUENTA ════════

export function MayorCuenta() {
  const [cuentas, setCuentas] = useState([])
  const [codigo, setCodigo] = useState('')
  const [desde, setDesde] = useState(() => firstDayMonth())
  const [hasta, setHasta] = useState(() => lastDayMonth())
  const [movimientos, setMovimientos] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    supabase.from('cuentas_contables').select('codigo, nombre, tipo').eq('es_imputable', true).order('codigo').then(({ data }) => setCuentas(data || []))
  }, [])

  useEffect(() => { if (codigo) cargar() }, [codigo, desde, hasta])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase.rpc('get_mayor_cuenta', { p_codigo: codigo, p_desde: desde, p_hasta: hasta })
    setMovimientos(data || [])
    setLoading(false)
  }

  const cuentaSel = cuentas.find(c => c.codigo === codigo)
  const totales = useMemo(() => {
    let debe = 0, haber = 0
    for (const m of movimientos) { debe += Number(m.debe); haber += Number(m.haber) }
    return { debe, haber, saldo: movimientos.length > 0 ? Number(movimientos[movimientos.length - 1].saldo) : 0 }
  }, [movimientos])

  return (
    <>
      <div className="rc-toolbar">
        <label className="rc-fil" style={{ flex: 1, maxWidth: 400 }}>
          <span>Cuenta contable</span>
          <select value={codigo} onChange={e => setCodigo(e.target.value)}>
            <option value="">— Elegí una cuenta —</option>
            {cuentas.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>)}
          </select>
        </label>
        <label className="rc-fil"><span>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} /></label>
        <label className="rc-fil"><span>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} /></label>
      </div>

      {!codigo ? (
        <div className="rc-empty"><strong>Elegí una cuenta arriba</strong><p>El mayor te muestra todos los movimientos de una cuenta puntual con saldo acumulado.</p></div>
      ) : loading ? <div className="rc-loading">Cargando…</div> :
       movimientos.length === 0 ? (
        <div className="rc-empty"><strong>Sin movimientos</strong><p>La cuenta {codigo} no tiene movimientos en este período.</p></div>
      ) : (
        <>
          <div className="rc-kpis">
            <KpiCard label="Total Debe" value={'$' + fmt(totales.debe)} color="#dc2626" />
            <KpiCard label="Total Haber" value={'$' + fmt(totales.haber)} color="#16a34a" />
            <KpiCard label="Saldo final" value={'$' + fmt(totales.saldo)} color="#1A3461" sub={cuentaSel?.nombre} />
            <KpiCard label="Movimientos" value={movimientos.length} color="#475569" />
          </div>

          <div className="rc-tabla-wrap">
            <table className="rc-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>#</th>
                  <th>Concepto</th>
                  <th>Detalle</th>
                  <th style={{ textAlign: 'right' }}>Debe</th>
                  <th style={{ textAlign: 'right' }}>Haber</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map((m, i) => (
                  <tr key={`${m.asiento_id}-${i}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{m.fecha}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>#{m.asiento_numero}</td>
                    <td>{m.concepto}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{m.detalle}</td>
                    <td style={{ textAlign: 'right', color: m.debe > 0 ? '#dc2626' : '#cbd5e1' }}>{m.debe > 0 ? '$' + fmt(m.debe) : '—'}</td>
                    <td style={{ textAlign: 'right', color: m.haber > 0 ? '#16a34a' : '#cbd5e1' }}>{m.haber > 0 ? '$' + fmt(m.haber) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>${fmt(m.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

// ════════ DIARIO ════════

export function Diario() {
  const [desde, setDesde] = useState(() => firstDayMonth())
  const [hasta, setHasta] = useState(() => lastDayMonth())
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => { cargar() }, [desde, hasta])

  async function cargar() {
    setLoading(true)
    const { data: rows } = await supabase.from('v_diario_contable').select('*').gte('fecha', desde).lte('fecha', hasta).order('numero')
    setData(rows || [])
    setLoading(false)
  }

  const filtrado = useMemo(() => {
    if (!busqueda.trim()) return data
    const q = busqueda.toLowerCase()
    return data.filter(r =>
      (r.concepto || '').toLowerCase().includes(q) ||
      (r.detalle || '').toLowerCase().includes(q) ||
      (r.cuenta_codigo || '').toLowerCase().includes(q) ||
      (r.cuenta_nombre || '').toLowerCase().includes(q)
    )
  }, [data, busqueda])

  // Agrupar por asiento_id
  const grupos = useMemo(() => {
    const m = new Map()
    for (const r of filtrado) {
      if (!m.has(r.asiento_id)) m.set(r.asiento_id, { cabecera: r, lineas: [] })
      m.get(r.asiento_id).lineas.push(r)
    }
    return Array.from(m.values())
  }, [filtrado])

  return (
    <>
      <div className="rc-toolbar">
        <label className="rc-fil"><span>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} /></label>
        <label className="rc-fil"><span>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} /></label>
        <label className="rc-fil" style={{ flex: 1 }}><span>Buscar</span><input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Concepto, cuenta, detalle…" /></label>
      </div>

      {loading ? <div className="rc-loading">Cargando…</div> :
       grupos.length === 0 ? <div className="rc-empty"><strong>Sin asientos en el período</strong></div> : (
        <div className="rc-diario">
          {grupos.map(g => (
            <div className="rc-asiento" key={g.cabecera.asiento_id}>
              <div className="rc-asiento-head">
                <div className="rc-asiento-num">#{g.cabecera.numero}</div>
                <div className="rc-asiento-fecha">{g.cabecera.fecha}</div>
                <div className="rc-asiento-concepto">{g.cabecera.concepto}</div>
                <div className="rc-asiento-monto">${fmt(g.cabecera.monto_total)}</div>
              </div>
              <table className="rc-asiento-tabla">
                <tbody>
                  {g.lineas.map(l => (
                    <tr key={l.linea_id}>
                      <td style={{ width: 100 }}><code>{l.cuenta_codigo}</code></td>
                      <td>{l.cuenta_nombre}</td>
                      <td style={{ fontSize: 11, color: '#64748b' }}>{l.detalle}</td>
                      <td style={{ textAlign: 'right', color: l.debe > 0 ? '#dc2626' : '#cbd5e1', width: 110 }}>{l.debe > 0 ? '$' + fmt(l.debe) : '—'}</td>
                      <td style={{ textAlign: 'right', color: l.haber > 0 ? '#16a34a' : '#cbd5e1', width: 110 }}>{l.haber > 0 ? '$' + fmt(l.haber) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function KpiCard({ label, value, sub, color }) {
  return <div className="rc-kpi"><div className="rc-kpi-l">{label}</div><div className="rc-kpi-v" style={{ color }}>{value}</div>{sub && <div className="rc-kpi-s">{sub}</div>}</div>
}

export const CSS = `
.rc-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.rc-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.rc-headline p { font-size: 13px; color: #64748b; margin: 0 0 16px }
.rc-tabs { display: flex; gap: 4px; margin-bottom: 16px; border-bottom: 2px solid #e2e8f0; flex-wrap: wrap }
.rc-tabs button { background: transparent; border: 0; padding: 10px 16px; font-size: 13px; font-weight: 700; color: #64748b; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; font-family: inherit }
.rc-tabs button.active { color: #1A3461; border-bottom-color: #1A3461 }
.rc-toolbar { display: flex; gap: 10px; align-items: flex-end; margin-bottom: 14px; flex-wrap: wrap }
.rc-fil { display: flex; flex-direction: column; gap: 3px }
.rc-fil span { font-size: 11px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.rc-fil input, .rc-fil select { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit; color: #1A3461; min-width: 160px }
.rc-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #64748b; padding: 7px 12px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit }
.rc-btn-ghost:hover { background: #f1f5f9 }

.rc-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 16px }
.rc-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.rc-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.rc-kpi-v { font-size: 22px; font-weight: 900 }
.rc-kpi-s { font-size: 11px; color: #64748b; margin-top: 2px }

.rc-loading, .rc-empty { padding: 40px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.rc-empty strong { display: block; color: #1A3461; font-size: 15px; margin-bottom: 6px }

.rc-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.rc-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 800px }
.rc-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.rc-tabla td { padding: 7px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.rc-tabla code { background: #e0e7ff; color: #3730a3; padding: 1px 5px; border-radius: 3px; font-size: 11px }
.rc-row-total { background: #f8fafc }
.rc-row-resultado { background: #1A3461; color: #fff }
.rc-row-resultado td { color: #fff }
.rc-expand { background: transparent; border: 0; cursor: pointer; padding: 0; font-size: 9px; color: #64748b; width: 14px }

.rc-info { margin-top: 18px; padding: 12px 16px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.5 }
.rc-info strong { font-size: 13px }

/* Diario */
.rc-diario { display: flex; flex-direction: column; gap: 10px }
.rc-asiento { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden }
.rc-asiento-head { display: grid; grid-template-columns: 70px 100px 1fr 120px; gap: 12px; align-items: center; padding: 10px 14px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; font-size: 13px }
.rc-asiento-num { font-family: ui-monospace, monospace; font-size: 11px; color: #64748b; font-weight: 800 }
.rc-asiento-fecha { font-size: 12px; color: #64748b }
.rc-asiento-concepto { font-weight: 700; color: #1A3461 }
.rc-asiento-monto { text-align: right; font-weight: 800; color: #1A3461 }
.rc-asiento-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px }
.rc-asiento-tabla td { padding: 6px 14px; border-bottom: 1px solid #f1f5f9 }
.rc-asiento-tabla tr:last-child td { border-bottom: 0 }

.rc-kpis-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; margin-bottom: 18px }
.rc-kpi-card { border: 2px solid; border-radius: 14px; padding: 16px 18px; min-height: 140px; display: flex; flex-direction: column }
.rc-kpi-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px }
.rc-kpi-name { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 800 }
.rc-kpi-valor { font-size: 36px; font-weight: 900; line-height: 1; margin-bottom: 8px }
.rc-kpi-formula { font-size: 11px; color: #475569; font-style: italic; margin-bottom: 6px }
.rc-kpi-detalle { font-size: 11.5px; color: #475569; margin-top: auto; line-height: 1.35 }

.rc-dot { display: inline-block; width: 11px; height: 11px; border-radius: 50%; vertical-align: middle }
`
